/**
 * Backup Reports API client (Spec 201 — observability)
 *
 * On-demand report download (Compliance / Completion, PDF / Excel) + CRUD for
 * scheduled, auto-emailed reports.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

export type ReportType = string; // any key in the report catalog (see reportsApi.types())
export type ReportFormat = 'pdf' | 'excel';
export type ReportFrequency = 'daily' | 'weekly' | 'monthly';
export type ReportSendStatus = 'success' | 'failed' | 'skipped';

export interface ReportTypeMeta {
  key: string;
  label: string;
  description: string;
  audience: 'Operations' | 'Compliance' | 'Capacity' | 'Executive';
}

export interface ScheduledReport {
  id: string;
  name: string;
  reportType: ReportType;
  format: ReportFormat;
  frequency: ReportFrequency;
  hourUtc: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  periodDays: number;
  recipients: string[];
  enabled: boolean;
  lastSentAt: string | null;
  lastStatus: ReportSendStatus | null;
  lastError: string | null;
  nextSendAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledReportInput {
  name: string;
  reportType: ReportType;
  format: ReportFormat;
  frequency: ReportFrequency;
  hourUtc: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  periodDays: number;
  recipients: string[];
  enabled: boolean;
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/reports';

/** Trigger a browser download of an on-demand report. Uses the authenticated client (Bearer token)
 *  — a raw fetch with credentials:'include' 401s because the webapp authenticates with a token, not
 *  a cookie. */
export async function downloadReport(
  type: ReportType,
  format: ReportFormat,
  startDate: string,
  endDate: string
): Promise<void> {
  const { blob, filename } = await apiClient.getBlob(`${BASE}/generate/${type}`, { startDate, endDate, format });
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename || `${type}-report-${startDate}-${endDate}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export const reportsApi = {
  types: async (): Promise<ReportTypeMeta[]> =>
    (await apiClient.get<Wrapped<ReportTypeMeta[]>>(`${BASE}/types`)).data,

  listSchedules: async (): Promise<ScheduledReport[]> =>
    (await apiClient.get<Wrapped<ScheduledReport[]>>(`${BASE}/schedules`)).data,

  createSchedule: async (input: ScheduledReportInput): Promise<ScheduledReport> =>
    (await apiClient.post<Wrapped<ScheduledReport>>(`${BASE}/schedules`, input)).data,

  updateSchedule: async (id: string, input: ScheduledReportInput): Promise<ScheduledReport> =>
    (await apiClient.put<Wrapped<ScheduledReport>>(`${BASE}/schedules/${id}`, input)).data,

  deleteSchedule: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/schedules/${id}`);
  },

  testSchedule: async (
    id: string
  ): Promise<{ status: ReportSendStatus; error: string | null }> =>
    (await apiClient.post<Wrapped<{ status: ReportSendStatus; error: string | null }>>(
      `${BASE}/schedules/${id}/test`,
      {}
    )).data,
};

/** Human-readable cadence summary for display. */
export function frequencySummary(s: Pick<ScheduledReport, 'frequency' | 'hourUtc' | 'dayOfWeek' | 'dayOfMonth'>): string {
  const hh = String(s.hourUtc).padStart(2, '0');
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (s.frequency === 'daily') return `Daily at ${hh}:00 UTC`;
  if (s.frequency === 'weekly') return `Weekly on ${days[s.dayOfWeek ?? 1]} at ${hh}:00 UTC`;
  return `Monthly on day ${s.dayOfMonth ?? 1} at ${hh}:00 UTC`;
}
